import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { suppliersApi as S } from "@/lib/suppliers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Search, Building2 } from "lucide-react";

const FIELDS = [
  ["name", "Supplier name"], ["contact_person", "Contact person"], ["mobile", "Mobile"],
  ["email", "Email"], ["gst_number", "GST number"], ["pan", "PAN"],
  ["payment_terms", "Payment terms"], ["bank_details", "Bank details"],
];

export default function Suppliers() {
  const { can } = useAuth();
  const canCreate = can("suppliers", "create");
  const canEdit = can("suppliers", "edit");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = () => { setLoading(true); S.list({ search: search || undefined, status: status === "all" ? undefined : status }).then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Suppliers</h2>
          <p className="mt-1 text-sm text-slate-500">Supplier master for purchase orders and goods receipt.</p>
        </div>
        {canCreate && <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-supplier-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" />Add Supplier</Button>}
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / code / GST" className="pl-9" data-testid="supplier-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="supplier-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
          </Select>
        </div>
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Code</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Name</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Contact</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">GST</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
                {canEdit && <TableHead />}
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`supplier-row-${r.code}`}>
                    <TableCell className="font-medium text-slate-800">{r.code}</TableCell>
                    <TableCell className="text-slate-700">{r.name}</TableCell>
                    <TableCell className="text-slate-600">{r.contact_person || "—"}<span className="block text-xs text-slate-400">{r.mobile}</span></TableCell>
                    <TableCell className="text-slate-500">{r.gst_number || "—"}</TableCell>
                    <TableCell><Badge className={r.is_active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>{r.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                    {canEdit && <TableCell><Button size="sm" variant="outline" onClick={() => { setEdit(r); setOpen(true); }} data-testid={`edit-supplier-${r.code}`}>Edit</Button></TableCell>}
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400" data-testid="suppliers-empty">No suppliers yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      <SupplierDialog open={open} onOpenChange={setOpen} supplier={edit} onDone={load} />
    </div>
  );
}

function SupplierDialog({ open, onOpenChange, supplier, onDone }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(supplier ? { ...supplier } : { is_active: true }); }, [open, supplier]);
  const submit = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      if (supplier) await S.update(supplier.id, f); else await S.create(f);
      toast.success(supplier ? "Supplier updated" : "Supplier added");
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-indigo-600" />{supplier ? "Edit Supplier" : "Add Supplier"}</DialogTitle><DialogDescription>Master record used across Purchase Orders and GRN.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, label]) => (
            <div key={k} className="space-y-1.5"><Label className="text-xs">{label}</Label><Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`supplier-${k}`} /></div>
          ))}
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Address</Label><Textarea value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="supplier-address" /></div>
          {supplier && (
            <div className="space-y-1.5"><Label className="text-xs">Status</Label>
              <Select value={f.is_active ? "active" : "inactive"} onValueChange={(v) => setF({ ...f, is_active: v === "active" })}>
                <SelectTrigger data-testid="supplier-active-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="submit-supplier-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
